/**
 * Ground surface materials and the paint map that records them (milestone 5.6 / spec §6.4).
 *
 * ## Why a texture and not per-vertex weights
 *
 * The obvious implementation — one material weight per terrain vertex — ties the paint resolution to
 * the mesh. At the shipped templates that is a vertex every ~0.3 m, so the edge of a mulch bed could
 * only ever be accurate to a foot, and raising the mesh density to fix it would cost geometry for a
 * problem that is not geometric.
 *
 * So the paint lives in its own regular grid in the terrain's UV space, at a resolution chosen for
 * the *paint* (≈8 texels per metre, ~12 cm). Two consequences fall out of that choice and both are
 * wanted: sculpting the ground never disturbs the paint (heights and weights are independent), and
 * the brush's cost is bounded by its own footprint rather than by the mesh under it.
 *
 * ## The weight model
 *
 * Weights are stored as bytes in [0, 255], one per painted layer, packed four to an RGBA **page**.
 * Grass is **not** stored: it is whatever weight is left over, `1 - sum(every channel of every
 * page)`. That is what makes grass the eraser — scaling every channel down is exactly "grow the lawn
 * back" — and it means an unpainted site costs nothing to store and needs no initialisation pass.
 *
 * The invariant every edit must preserve is `sum(channels) <= 255`. {@link compositeLayerWeight} is
 * the only function allowed to write weights, and it holds that invariant by construction, so no
 * caller can produce a texel that is 80 % water and 80 % blacktop.
 *
 * ## Why pages, and why the palette is not a constant
 *
 * The five shipped materials fitted one RGBA texture exactly, which is a suspicious kind of tidy: it
 * meant the *format* decided how many materials a garden could have. A site that needs red gravel,
 * bluestone and decomposed granite is not asking for anything exotic, and "there are four channels"
 * is not an answer. So the palette is a {@link SurfacePalette} that can be added to at runtime — by
 * the texture forge, by an uploaded swatch, by the reconstruction pipeline — and the map carries as
 * many pages as the palette currently needs, up to {@link MAX_PAINTED_LAYERS}.
 *
 * The palette is a module singleton rather than a value threaded through every call site. That is a
 * deliberate trade: one project is open at a time (`Terrain`, the paint tool and the history all
 * already assume it), and threading a palette handle through twenty modules to model a plurality
 * that does not exist would cost more than it buys. Loading a project replaces the custom entries;
 * the five built-ins are code and are always present.
 */

/**
 * A layer's id. Free-form because the palette is open: `grass` and `blacktop` are shipped, and
 * `red-gravel` may be minted at runtime by the forge.
 */
export type SurfaceLayerId = string;

/** Fraction of the site under each layer, keyed by id. Sums to 1. */
export type SurfaceCoverage = Record<SurfaceLayerId, number>;

/**
 * A tiled albedo image for a layer.
 *
 * Held as a data URL rather than a blob so that a layer is a plain JSON value: it goes into the
 * project document, into an undo record and across a `postMessage` without a side table of binary
 * handles to keep in step. Swatches are normalised to {@link SWATCH_TEXELS} on the way in, so the
 * size is bounded and every layer's image can share one GPU array texture.
 */
export interface SurfaceSwatch {
  /** Square RGBA image, `data:image/png;base64,…`, {@link SWATCH_TEXELS} on a side. */
  dataUrl: string;
  /** Metres one tile of the swatch covers on the ground. */
  tileMeters: number;
}

/** How a layer came to exist. Absent on the five shipped materials. */
export interface SurfaceLayerOrigin {
  /**
   * `forged` came from a model; `uploaded` came from a file the user chose; `stock` was taken from
   * the shipped ground-texture catalog.
   *
   * `stock` is worth distinguishing from `uploaded` even though both arrive as finished pixels,
   * because it is the only one of the three that is *reproducible*: the swatch is still in the
   * build, under the same id, so a document carrying a stock layer can be re-linked to its catalog
   * entry. An upload cannot be — the file it came from was on someone's laptop.
   */
  kind: 'forged' | 'uploaded' | 'stock';
  /** The natural-language request, the file name, or the catalog id. Shown in the panel. */
  request: string;
  /** What the grounding search established — real colours, grain size, trade names. */
  grounding?: string;
  /** ISO timestamp. */
  createdAt: string;
}

export interface SurfaceLayer {
  id: SurfaceLayerId;
  name: string;
  /**
   * Splat slot, `0 .. MAX_PAINTED_LAYERS - 1`, or `-1` for the base layer, which is never stored.
   * Page is `channel >> 2`, RGBA component is `channel & 3`.
   */
  channel: number;
  /**
   * Surface colour as `#rrggbb`. The layer's average — it is what the chip, the brush ring and the
   * plan overlay draw, and what the shader uses when there is no swatch.
   */
  color: string;
  /** Roughness the shader blends toward where this layer dominates. Water is the shiny one. */
  roughness: number;
  /** Whether the placement tool will accept a plant here. */
  plantable: boolean;
  /** One line for the tool panel. */
  hint: string;
  /** True for the five shipped materials, which cannot be removed or edited. */
  builtIn: boolean;
  /** Tiled albedo, when the layer has one. Built-ins do not. */
  swatch?: SurfaceSwatch;
  /**
   * What this material *is*, in the language a render prompt wants — "loose angular gravel, mid-grey,
   * compacted where it is walked on". Feeds the capture manifest, which otherwise has only a name.
   */
  character?: string;
  origin?: SurfaceLayerOrigin;
}

/** Everything needed to mint a layer. The palette assigns the id and the channel. */
export interface SurfaceLayerDraft {
  name: string;
  color: string;
  roughness: number;
  plantable: boolean;
  hint: string;
  /** Preferred id. Slugified, and suffixed if it is taken. */
  id?: string;
  swatch?: SurfaceSwatch;
  character?: string;
  origin?: SurfaceLayerOrigin;
}

/* --------------------------------- The palette --------------------------------- */

/** RGBA. One page carries four painted layers. */
export const SPLAT_CHANNELS_PER_PAGE = 4;

/**
 * Pages the shader will compile for.
 *
 * Four is a judgement, not a limit of the technique: sixteen materials is already more than a garden
 * reads as distinct, and every page costs one array-texture fetch per ground fragment whether it is
 * used or not. The palette refuses a seventeenth layer with a message that says which one to remove,
 * which is a better failure than a silently ignored request.
 */
export const MAX_SPLAT_PAGES = 4;

/** Painted layers the palette can hold. Grass is on top of this, as the unstored base. */
export const MAX_PAINTED_LAYERS = SPLAT_CHANNELS_PER_PAGE * MAX_SPLAT_PAGES;

/**
 * Side length every swatch is normalised to, so one array texture can hold them all.
 *
 * 512 rather than 256. A swatch tiles at ~1.5 m, so at 256 a paving slab got about 170 texels across
 * its face — enough for the colour and the grain, not enough for the joint between two slabs to stay
 * a line rather than a smudge once the camera came down to standing height. The whole palette is
 * `SWATCH_TEXELS² × 4 bytes × MAX_PAINTED_LAYERS`: 4 MB at 256, 16 MB at 512 (plus a third again for
 * the mip chain), which is a fair price on any GPU that can run this at all. 1024 would be another
 * 64 MB to resolve detail the mip chain discards at the angles ground is actually seen from.
 */
export const SWATCH_TEXELS = 512;

/** Default metres one swatch tile covers, when nothing better is known. */
export const DEFAULT_TILE_METERS = 1.5;

/** Pages needed to store `count` painted layers. Always at least one. */
export function splatPagesFor(count: number): number {
  return Math.min(
    MAX_SPLAT_PAGES,
    Math.max(1, Math.ceil(Math.max(0, count) / SPLAT_CHANNELS_PER_PAGE)),
  );
}

/** The base layer — the one that is implied by the absence of the others. */
export const BASE_SURFACE_LAYER: SurfaceLayer = {
  id: 'grass',
  name: 'Grass',
  channel: -1,
  color: '#7ba05b',
  roughness: 0.95,
  plantable: true,
  hint: 'Lawn — and the eraser: painting grass takes every other layer back out.',
  builtIn: true,
  character: 'mown lawn turf, with mower lines and a soft edge where it meets planting',
};

/** The four materials that shipped with the paint tool, in their original channel order. */
export const BUILT_IN_PAINTED_LAYERS: readonly SurfaceLayer[] = [
  {
    id: 'mulch',
    name: 'Mulch',
    channel: 0,
    color: '#4a3527',
    roughness: 0.98,
    plantable: true,
    hint: 'Bark bed. Plantable — this is what you draw a border with.',
    builtIn: true,
    character: 'shredded bark mulch, dark and coarse, dressed evenly around planting',
  },
  {
    id: 'water',
    name: 'Water',
    channel: 1,
    color: '#2f5f78',
    roughness: 0.06,
    plantable: false,
    hint: 'Pond or stream surface. Refuses planting.',
    builtIn: true,
    character: 'still open water, reflective, with a defined margin',
  },
  {
    id: 'blacktop',
    name: 'Blacktop',
    channel: 2,
    color: '#3a393c',
    roughness: 0.55,
    plantable: false,
    hint: 'Asphalt drive or path. Refuses planting.',
    builtIn: true,
    character: 'rolled asphalt, matte black-grey, slightly dished for drainage',
  },
  {
    id: 'gravel',
    name: 'Gravel',
    channel: 3,
    color: '#9c968b',
    roughness: 0.88,
    plantable: true,
    hint: 'Loose stone. Plantable, for gravel gardens and dry beds.',
    builtIn: true,
    character: 'loose angular gravel, mid-grey, compacted where it is walked on',
  },
];

/** Thrown when every splat slot is spoken for. Carries a message meant for the user. */
export class SurfacePaletteFullError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SurfacePaletteFullError';
  }
}

/**
 * The set of materials this site can be painted with.
 *
 * Ordered by channel so the palette reads the same as the splat: adding a layer appends, removing
 * one frees its slot for the next addition rather than renumbering everything after it. Renumbering
 * would be tidier to look at and catastrophic in practice — every byte already painted on the site
 * refers to a channel, and moving a layer between channels would repaint the garden.
 */
export class SurfacePalette {
  private layers: SurfaceLayer[] = BUILT_IN_PAINTED_LAYERS.map(cloneLayer);
  private readonly listeners = new Set<() => void>();
  /** Increments on every change, so the renderer can decide whether to rebuild its textures. */
  revision = 0;

  /** Every layer, base first, then the painted ones in channel order. */
  all(): readonly SurfaceLayer[] {
    return [BASE_SURFACE_LAYER, ...this.painted()];
  }

  /** The painted layers, in channel order. */
  painted(): readonly SurfaceLayer[] {
    return [...this.layers].sort((a, b) => a.channel - b.channel);
  }

  /** Layers the user added — forged or uploaded — in the order they were added. */
  custom(): readonly SurfaceLayer[] {
    return this.painted().filter((layer) => !layer.builtIn);
  }

  ids(): SurfaceLayerId[] {
    return this.all().map((layer) => layer.id);
  }

  find(id: SurfaceLayerId): SurfaceLayer | undefined {
    if (id === BASE_SURFACE_LAYER.id) return BASE_SURFACE_LAYER;
    return this.layers.find((layer) => layer.id === id);
  }

  has(id: SurfaceLayerId): boolean {
    return this.find(id) !== undefined;
  }

  /** Splat pages the current palette needs. */
  get pages(): number {
    const highest = this.layers.reduce((max, layer) => Math.max(max, layer.channel), -1);
    return splatPagesFor(highest + 1);
  }

  /** Painted slots still free. */
  get freeSlots(): number {
    return MAX_PAINTED_LAYERS - this.layers.length;
  }

  /**
   * Mint a layer and give it the lowest free channel.
   *
   * Lowest free rather than next-highest so that removing a layer and adding another does not creep
   * the page count upward across a working session — a site that has only ever held six materials
   * should never be paying to sample a fourth page.
   */
  add(draft: SurfaceLayerDraft): SurfaceLayer {
    const channel = this.lowestFreeChannel();
    if (channel < 0) {
      throw new SurfacePaletteFullError(
        `This site already has ${MAX_PAINTED_LAYERS} painted materials, which is the most the ground ` +
          'shader can blend. Remove one from the ground panel to make room.',
      );
    }

    const layer: SurfaceLayer = {
      id: this.uniqueId(draft.id ?? draft.name),
      name: draft.name.trim() || 'Untitled material',
      channel,
      color: normalizeHex(draft.color) ?? '#888888',
      roughness: clamp(draft.roughness, 0, 1),
      plantable: draft.plantable,
      hint: draft.hint.trim(),
      builtIn: false,
      ...(draft.swatch ? { swatch: { ...draft.swatch } } : {}),
      ...(draft.character ? { character: draft.character } : {}),
      ...(draft.origin ? { origin: { ...draft.origin } } : {}),
    };
    this.layers.push(layer);
    this.emit();
    return layer;
  }

  /**
   * Remove a custom layer, freeing its channel.
   *
   * The weights already painted in that channel are **not** cleared here: the map does not know the
   * palette, and clearing them is the caller's business — see `Terrain.retireSurfaceLayer`, which
   * does it as an undoable edit. Refuses the built-ins, which the rest of the app treats as always
   * present.
   */
  remove(id: SurfaceLayerId): boolean {
    const index = this.layers.findIndex((layer) => layer.id === id && !layer.builtIn);
    if (index < 0) return false;
    this.layers.splice(index, 1);
    this.emit();
    return true;
  }

  /** Edit a custom layer in place. Ignores `id`, `channel` and `builtIn`. */
  update(id: SurfaceLayerId, changes: Partial<SurfaceLayerDraft>): SurfaceLayer | undefined {
    const layer = this.layers.find((entry) => entry.id === id && !entry.builtIn);
    if (!layer) return undefined;
    if (changes.name !== undefined) layer.name = changes.name.trim() || layer.name;
    if (changes.color !== undefined) layer.color = normalizeHex(changes.color) ?? layer.color;
    if (changes.roughness !== undefined) layer.roughness = clamp(changes.roughness, 0, 1);
    if (changes.plantable !== undefined) layer.plantable = changes.plantable;
    if (changes.hint !== undefined) layer.hint = changes.hint.trim();
    if (changes.character !== undefined) layer.character = changes.character;
    if (changes.swatch !== undefined) layer.swatch = { ...changes.swatch };
    this.emit();
    return layer;
  }

  /** Back to the five shipped materials. Called when a project is closed or a new site is started. */
  reset(): void {
    this.layers = BUILT_IN_PAINTED_LAYERS.map(cloneLayer);
    this.emit();
  }

  /**
   * Replace the custom layers with a document's.
   *
   * Channels are honoured as stored rather than reassigned, because the paint map that travels with
   * them refers to those channels by number. A layer whose channel collides with a built-in's, or
   * with another custom layer's, is dropped — its paint is unreachable either way, and silently
   * moving it would repaint someone's garden with the wrong material.
   */
  load(state: SurfacePaletteState | undefined): void {
    this.layers = BUILT_IN_PAINTED_LAYERS.map(cloneLayer);
    for (const entry of state?.layers ?? []) {
      const channel = Math.trunc(entry.channel);
      if (!Number.isFinite(channel) || channel < 0 || channel >= MAX_PAINTED_LAYERS) continue;
      if (this.layers.some((layer) => layer.channel === channel)) continue;
      const id = entry.id.trim();
      if (id.length === 0 || this.has(id)) continue;
      this.layers.push({
        id,
        name: entry.name.trim() || id,
        channel,
        color: normalizeHex(entry.color) ?? '#888888',
        roughness: clamp(entry.roughness, 0, 1),
        plantable: entry.plantable !== false,
        hint: entry.hint ?? '',
        builtIn: false,
        ...(entry.swatch
          ? {
              swatch: {
                dataUrl: entry.swatch.dataUrl,
                tileMeters: clamp(entry.swatch.tileMeters, 0.05, 40),
              },
            }
          : {}),
        ...(entry.character ? { character: entry.character } : {}),
        ...(entry.origin ? { origin: { ...entry.origin } } : {}),
      });
    }
    this.emit();
  }

  /** The custom layers, for the project document. Built-ins are code and are not written out. */
  toState(): SurfacePaletteState {
    return {
      layers: this.custom().map((layer) => ({
        id: layer.id,
        name: layer.name,
        channel: layer.channel,
        color: layer.color,
        roughness: layer.roughness,
        plantable: layer.plantable,
        hint: layer.hint,
        ...(layer.swatch ? { swatch: { ...layer.swatch } } : {}),
        ...(layer.character ? { character: layer.character } : {}),
        ...(layer.origin ? { origin: { ...layer.origin } } : {}),
      })),
    };
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private lowestFreeChannel(): number {
    const taken = new Set(this.layers.map((layer) => layer.channel));
    for (let channel = 0; channel < MAX_PAINTED_LAYERS; channel += 1) {
      if (!taken.has(channel)) return channel;
    }
    return -1;
  }

  /** Slugify, then suffix until free. `red gravel` → `red-gravel`, `red-gravel-2`, … */
  private uniqueId(seed: string): SurfaceLayerId {
    const base =
      seed
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40) || 'material';
    if (!this.has(base)) return base;
    for (let n = 2; n < 1000; n += 1) {
      const candidate = `${base}-${n}`;
      if (!this.has(candidate)) return candidate;
    }
    return `${base}-${Date.now()}`;
  }

  private emit(): void {
    this.revision += 1;
    for (const listener of this.listeners) listener();
  }
}

/** One custom layer as it goes into the project document. */
export interface SurfaceLayerState {
  id: string;
  name: string;
  channel: number;
  color: string;
  roughness: number;
  plantable: boolean;
  hint: string;
  swatch?: SurfaceSwatch;
  character?: string;
  origin?: SurfaceLayerOrigin;
}

export interface SurfacePaletteState {
  layers: SurfaceLayerState[];
}

/** The one palette. See the module note on why this is a singleton. */
export const surfacePalette = new SurfacePalette();

/** Every layer, base first. */
export function surfaceLayers(): readonly SurfaceLayer[] {
  return surfacePalette.all();
}

/** The painted layers, in channel order. */
export function paintedSurfaceLayers(): readonly SurfaceLayer[] {
  return surfacePalette.painted();
}

export function surfaceLayerIds(): SurfaceLayerId[] {
  return surfacePalette.ids();
}

/** A layer by id, or `undefined`. Use this wherever the id came from stored data. */
export function findSurfaceLayer(id: SurfaceLayerId): SurfaceLayer | undefined {
  return surfacePalette.find(id);
}

/** A layer by id. Throws — for callers that got the id from the palette in the first place. */
export function getSurfaceLayer(id: SurfaceLayerId): SurfaceLayer {
  const layer = surfacePalette.find(id);
  if (!layer) throw new Error(`Unknown surface layer: ${id}`);
  return layer;
}

/** Whether a plant may be placed where this layer dominates. Unknown ids read as plantable. */
export function isPlantableSurface(id: SurfaceLayerId): boolean {
  return surfacePalette.find(id)?.plantable ?? true;
}

/* --------------------------------- The paint map --------------------------------- */

/** A rectangle in texel space. `width`/`height` of 0 means "nothing". */
export interface SurfaceRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Texels per metre. ~8 cm per texel.
 *
 * Raised from 8 (12 cm). The paint's resolution is what decides how crisp the *edge* of a bed or a
 * path is, and 12 cm was visible as a soft stair-step along a straight paving edge from any camera
 * close enough to walk the garden. The cost is quadratic but small in absolute terms — a 40 m site
 * goes from a 320² map to 480², i.e. 400 KB to 920 KB per page — and it is bounded by
 * {@link MAX_SURFACE_RESOLUTION} either way. A map saved at one density reopens at another: see
 * {@link deserializeSurfaceMap}, which resamples.
 */
export const SURFACE_TEXELS_PER_METER = 12;
export const MIN_SURFACE_RESOLUTION = 64;
export const MAX_SURFACE_RESOLUTION = 1024;

/**
 * Choose a paint resolution for a site. Square texels (so a brush is round in world space, not
 * elliptical) and clamped at both ends so neither a 5 m courtyard nor a 200 m estate is pathological.
 */
export function surfaceResolution(
  widthMeters: number,
  lengthMeters: number,
  texelsPerMeter = SURFACE_TEXELS_PER_METER,
): { width: number; height: number } {
  const clampAxis = (meters: number): number =>
    Math.min(
      MAX_SURFACE_RESOLUTION,
      Math.max(MIN_SURFACE_RESOLUTION, Math.round(Math.max(1, meters) * texelsPerMeter)),
    );
  const width = clampAxis(widthMeters);
  const height = clampAxis(lengthMeters);
  return { width, height };
}

/**
 * The painted ground of one site: `pages` RGBA byte grids in terrain UV space plus the world extent
 * they cover, so world↔texel conversion never needs a second source of truth.
 *
 * The bytes are **page-major** — page 0's whole grid, then page 1's — which is exactly the layout a
 * `DataArrayTexture` wants, so the GPU copy is the array itself with no repacking step. It also
 * makes {@link ensurePages} a prefix copy rather than an interleave.
 *
 * `revision` increments on every mutation. The renderer watches it to decide whether to re-upload
 * the texture, which keeps the view a pure function of this data.
 */
export class SurfaceMap {
  readonly width: number;
  readonly height: number;
  readonly widthMeters: number;
  readonly lengthMeters: number;
  /** Backed by a plain `ArrayBuffer` so it can be handed straight to a GPU texture. */
  data: Uint8Array<ArrayBuffer>;
  /** RGBA pages stored, `1 .. MAX_SPLAT_PAGES`. Four painted layers each. */
  pages: number;
  revision = 0;

  constructor(
    width: number,
    height: number,
    widthMeters: number,
    lengthMeters: number,
    data?: Uint8Array<ArrayBuffer>,
    pages = 1,
  ) {
    this.width = Math.max(1, Math.trunc(width));
    this.height = Math.max(1, Math.trunc(height));
    this.widthMeters = widthMeters;
    this.lengthMeters = lengthMeters;
    this.pages = Math.min(MAX_SPLAT_PAGES, Math.max(1, Math.trunc(pages)));
    const size = this.width * this.height * 4 * this.pages;
    this.data = data && data.length === size ? data : new Uint8Array(size);
  }

  /** A map sized for a site, entirely grass. */
  static forSite(
    widthMeters: number,
    lengthMeters: number,
    texelsPerMeter?: number,
    pages = 1,
  ): SurfaceMap {
    const { width, height } = surfaceResolution(widthMeters, lengthMeters, texelsPerMeter);
    return new SurfaceMap(width, height, widthMeters, lengthMeters, undefined, pages);
  }

  get texelCount(): number {
    return this.width * this.height;
  }

  /** Bytes one texel occupies across every page. */
  get bytesPerTexel(): number {
    return this.pages * 4;
  }

  /** Metres covered by one texel along X. */
  get texelWidthMeters(): number {
    return this.widthMeters / this.width;
  }

  /** Metres covered by one texel along Z. */
  get texelHeightMeters(): number {
    return this.lengthMeters / this.height;
  }

  /** Byte offset of one texel's RGBA on one page. */
  offset(texelIndex: number, page: number): number {
    return (page * this.texelCount + texelIndex) * 4;
  }

  /**
   * Grow to at least `pages`, preserving every weight. Returns whether anything changed.
   *
   * Page-major layout is what makes this a single `set` of the old buffer into the head of the new
   * one: page *p*'s data does not move, and the new pages start at zero, which is "no weight" — so a
   * garden painted before the palette grew is untouched by the growth.
   */
  ensurePages(pages: number): boolean {
    const target = Math.min(MAX_SPLAT_PAGES, Math.max(1, Math.trunc(pages)));
    if (target <= this.pages) return false;
    const next = new Uint8Array(this.width * this.height * 4 * target);
    next.set(this.data);
    this.data = next;
    this.pages = target;
    this.touch();
    return true;
  }

  /** True when nothing has been painted — the whole site is still the base layer. */
  isEmpty(): boolean {
    for (let i = 0; i < this.data.length; i += 1) {
      if (this.data[i] !== 0) return false;
    }
    return true;
  }

  /** Mark the data as changed. Callers that write `data` directly must call this. */
  touch(): void {
    this.revision += 1;
  }

  clear(): void {
    this.data.fill(0);
    this.touch();
  }

  clone(): SurfaceMap {
    return new SurfaceMap(
      this.width,
      this.height,
      this.widthMeters,
      this.lengthMeters,
      this.data.slice(),
      this.pages,
    );
  }

  /**
   * Zero one channel across the whole map — what retiring a layer has to do to the paint that
   * referred to it. Returns whether any weight was actually removed, so a no-op retirement records
   * no undo entry.
   */
  clearChannel(channel: number): boolean {
    const page = channel >> 2;
    const component = channel & 3;
    if (channel < 0 || page >= this.pages) return false;
    let changed = false;
    for (let i = 0; i < this.texelCount; i += 1) {
      const at = this.offset(i, page) + component;
      if (this.data[at] !== 0) {
        this.data[at] = 0;
        changed = true;
      }
    }
    if (changed) this.touch();
    return changed;
  }

  /**
   * Copy a rectangle out, page-major, row-major RGBA within each page. Used by the undo record to
   * store only what changed.
   */
  readRect(rect: SurfaceRect): Uint8Array {
    const stride = rect.width * 4;
    const out = new Uint8Array(stride * rect.height * this.pages);
    for (let page = 0; page < this.pages; page += 1) {
      const pageOut = page * stride * rect.height;
      for (let row = 0; row < rect.height; row += 1) {
        const src = this.offset((rect.y + row) * this.width + rect.x, page);
        out.set(this.data.subarray(src, src + stride), pageOut + row * stride);
      }
    }
    return out;
  }

  /**
   * Write a rectangle back. The inverse of {@link readRect}.
   *
   * Only as many pages as the payload carries are written, so an undo record taken before the
   * palette grew still applies cleanly — it restores the channels it knew about and leaves the newer
   * ones alone, which is the honest reading of "undo the stroke that was made".
   */
  writeRect(rect: SurfaceRect, pixels: Uint8Array): void {
    const stride = rect.width * 4;
    const plane = stride * rect.height;
    if (plane === 0) return;
    const pages = Math.min(this.pages, Math.floor(pixels.length / plane));
    for (let page = 0; page < pages; page += 1) {
      const pageIn = page * plane;
      for (let row = 0; row < rect.height; row += 1) {
        const dst = this.offset((rect.y + row) * this.width + rect.x, page);
        const src = pageIn + row * stride;
        this.data.set(pixels.subarray(src, src + stride), dst);
      }
    }
    this.touch();
  }
}

/* ------------------------------ Coordinate mapping ------------------------------ */

/**
 * Terrain-local (x, z) in metres → continuous texel coordinates. The grid is centred on the origin,
 * matching {@link buildTerrainGeometry}'s UV layout, so `u` runs 0→1 across `-width/2 → +width/2`.
 */
export function worldToTexel(map: SurfaceMap, x: number, z: number): { u: number; v: number } {
  return {
    u: (x / map.widthMeters + 0.5) * map.width,
    v: (z / map.lengthMeters + 0.5) * map.height,
  };
}

/** Texel centre → terrain-local metres. The inverse of {@link worldToTexel}. */
export function texelToWorld(map: SurfaceMap, col: number, row: number): { x: number; z: number } {
  return {
    x: ((col + 0.5) / map.width - 0.5) * map.widthMeters,
    z: ((row + 0.5) / map.height - 0.5) * map.lengthMeters,
  };
}

/** Per-layer weights in [0, 1] at a texel, base layer included. Clamped to the edge. */
export function weightsAtTexel(map: SurfaceMap, col: number, row: number): SurfaceCoverage {
  const c = Math.min(Math.max(Math.trunc(col), 0), map.width - 1);
  const r = Math.min(Math.max(Math.trunc(row), 0), map.height - 1);
  const index = r * map.width + c;
  const weights: SurfaceCoverage = {};
  let painted = 0;
  for (const layer of paintedSurfaceLayers()) {
    const page = layer.channel >> 2;
    const value =
      page < map.pages ? (map.data[map.offset(index, page) + (layer.channel & 3)] ?? 0) / 255 : 0;
    weights[layer.id] = value;
    painted += value;
  }
  weights[BASE_SURFACE_LAYER.id] = Math.max(0, 1 - painted);
  return weights;
}

/** Per-layer weights at a terrain-local world point. */
export function weightsAtWorld(map: SurfaceMap, x: number, z: number): SurfaceCoverage {
  const { u, v } = worldToTexel(map, x, z);
  return weightsAtTexel(map, Math.floor(u), Math.floor(v));
}

/**
 * The layer that reads as "what this ground is" at a point — the heaviest one.
 *
 * Ties go to grass, and a layer only wins outright at the point it is visually dominant, which is
 * the question the placement rule actually asks: may a plant go *here*. A texel that is 30 % water
 * at the feathered edge of a pond is still bank, and still plantable.
 */
export function dominantLayerAt(map: SurfaceMap, x: number, z: number): SurfaceLayerId {
  const weights = weightsAtWorld(map, x, z);
  let best: SurfaceLayerId = BASE_SURFACE_LAYER.id;
  let bestWeight = weights[BASE_SURFACE_LAYER.id] ?? 0;
  for (const layer of paintedSurfaceLayers()) {
    const w = weights[layer.id] ?? 0;
    if (w > bestWeight) {
      bestWeight = w;
      best = layer.id;
    }
  }
  return best;
}

/**
 * Fraction of the site covered by each layer, summing to 1. Reported in the site summary and in the
 * capture manifest, where "38 % blacktop" is a fact an image model can otherwise only guess at.
 */
export function surfaceCoverage(map: SurfaceMap): SurfaceCoverage {
  const painted = paintedSurfaceLayers();
  const totals: SurfaceCoverage = { [BASE_SURFACE_LAYER.id]: 0 };
  for (const layer of painted) totals[layer.id] = 0;

  const count = map.texelCount;
  if (count === 0) return totals;

  for (let i = 0; i < count; i += 1) {
    let sum = 0;
    for (const layer of painted) {
      const page = layer.channel >> 2;
      if (page >= map.pages) continue;
      const value = map.data[map.offset(i, page) + (layer.channel & 3)] ?? 0;
      totals[layer.id] = (totals[layer.id] ?? 0) + value;
      sum += value;
    }
    totals[BASE_SURFACE_LAYER.id] =
      (totals[BASE_SURFACE_LAYER.id] ?? 0) + Math.max(0, 255 - sum);
  }

  const scale = 1 / (count * 255);
  for (const id of Object.keys(totals)) totals[id] = (totals[id] ?? 0) * scale;
  return totals;
}

/* --------------------------------- Rectangles --------------------------------- */

export const EMPTY_RECT: SurfaceRect = { x: 0, y: 0, width: 0, height: 0 };

export function isEmptyRect(rect: SurfaceRect): boolean {
  return rect.width <= 0 || rect.height <= 0;
}

/** Clip a rectangle to a grid, returning an empty rect when nothing survives. */
export function clipRect(rect: SurfaceRect, width: number, height: number): SurfaceRect {
  const x0 = Math.max(0, Math.trunc(rect.x));
  const y0 = Math.max(0, Math.trunc(rect.y));
  const x1 = Math.min(width, Math.trunc(rect.x + rect.width));
  const y1 = Math.min(height, Math.trunc(rect.y + rect.height));
  if (x1 <= x0 || y1 <= y0) return { ...EMPTY_RECT };
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/** Smallest rectangle containing both. Either may be empty. */
export function unionRect(a: SurfaceRect, b: SurfaceRect): SurfaceRect {
  if (isEmptyRect(a)) return { ...b };
  if (isEmptyRect(b)) return { ...a };
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const right = Math.max(a.x + a.width, b.x + b.width);
  const bottom = Math.max(a.y + a.height, b.y + b.height);
  return { x, y, width: right - x, height: bottom - y };
}

/** Grow a rectangle by `margin` cells on every side, then clip. */
export function expandRect(
  rect: SurfaceRect,
  margin: number,
  width: number,
  height: number,
): SurfaceRect {
  if (isEmptyRect(rect)) return { ...EMPTY_RECT };
  return clipRect(
    {
      x: rect.x - margin,
      y: rect.y - margin,
      width: rect.width + margin * 2,
      height: rect.height + margin * 2,
    },
    width,
    height,
  );
}

/* --------------------------------- Compositing --------------------------------- */

/**
 * Add `weight` of one layer to a texel, keeping the sum of every channel on every page `<= 255`.
 *
 * Every other layer is scaled by `1 - weight` before the target channel gains `weight`, which is the
 * over operator on a partition of unity: the paint replaces what was there in proportion, rather
 * than stacking on top of it. Two things follow that the tool depends on — the base layer needs no
 * channel of its own (it is the remainder), and painting the base layer is simply the scale step
 * with nothing added back, i.e. an eraser.
 *
 * The scale step runs across *every* page, which is what keeps the invariant global rather than
 * per-page: without it, painting a layer on page 1 would leave page 0's weights untouched and a
 * texel could reach 200 % coverage.
 *
 * `channel` of -1 means the base layer.
 */
export function compositeLayerWeight(
  map: SurfaceMap,
  texelIndex: number,
  channel: number,
  weight: number,
): void {
  if (weight <= 0) return;
  const keep = 1 - Math.min(1, weight);
  const data = map.data;
  let sum = 0;
  for (let page = 0; page < map.pages; page += 1) {
    const offset = map.offset(texelIndex, page);
    for (let c = 0; c < 4; c += 1) {
      const scaled = Math.round((data[offset + c] ?? 0) * keep);
      data[offset + c] = scaled;
      sum += scaled;
    }
  }

  const page = channel >> 2;
  if (channel < 0 || page >= map.pages) return;
  const at = map.offset(texelIndex, page) + (channel & 3);
  // Rounding above can leave a byte or two of headroom below 255; never exceed it.
  const room = Math.max(0, 255 - sum);
  const added = Math.min(room, Math.round(255 * Math.min(1, weight)));
  data[at] = Math.min(255, (data[at] ?? 0) + added);
}

/** Total painted weight at a texel, 0–255. `255 - this` is the base layer's share. */
export function paintedWeightAt(map: SurfaceMap, texelIndex: number): number {
  let sum = 0;
  for (let page = 0; page < map.pages; page += 1) {
    const offset = map.offset(texelIndex, page);
    for (let c = 0; c < 4; c += 1) sum += map.data[offset + c] ?? 0;
  }
  return sum;
}

/* -------------------------------- Serialization -------------------------------- */

/**
 * The paint map as it goes into the project document.
 *
 * Run-length encoded over whole texels — every page's RGBA at once — then base64. A site is
 * overwhelmingly one material at a time, which is what ground *is*, so runs are long and a 512×512
 * map of a mostly-grass garden costs a few kilobytes rather than the megabyte the raw bytes would.
 * The width, height and page count travel with the data because the resolution is derived from the
 * site size and the page count from the palette, and either can differ in the build that reopens it.
 */
export interface TerrainSurfaceState {
  width: number;
  height: number;
  /** Splat pages stored. Absent means one — how every file written before layers were addable. */
  pages?: number;
  /** Only `rle-base64` today; named so a future encoding can be added without ambiguity. */
  encoding: 'rle-base64';
  data: string;
}

/** Encode as runs of `count:uint16le` + every page's RGBA, then base64. */
export function serializeSurfaceMap(map: SurfaceMap): TerrainSurfaceState {
  const stride = map.bytesPerTexel;
  const runs: number[] = [];
  const current = new Uint8Array(stride);
  const candidate = new Uint8Array(stride);
  let runLength = 0;

  const readTexel = (index: number, into: Uint8Array): void => {
    for (let page = 0; page < map.pages; page += 1) {
      const offset = map.offset(index, page);
      for (let c = 0; c < 4; c += 1) into[page * 4 + c] = map.data[offset + c] ?? 0;
    }
  };

  const flush = (): void => {
    if (runLength === 0) return;
    runs.push(runLength & 0xff, (runLength >> 8) & 0xff, ...current);
    runLength = 0;
  };

  for (let i = 0; i < map.texelCount; i += 1) {
    readTexel(i, candidate);
    if (runLength > 0 && runLength < 0xffff && sameBytes(candidate, current)) {
      runLength += 1;
      continue;
    }
    flush();
    current.set(candidate);
    runLength = 1;
  }
  flush();

  return {
    width: map.width,
    height: map.height,
    pages: map.pages,
    encoding: 'rle-base64',
    data: bytesToBase64(Uint8Array.from(runs)),
  };
}

/**
 * Rebuild a paint map from a project document.
 *
 * The stored grid is resampled (nearest neighbour) onto the resolution this build would choose for
 * the site, so a file saved at one texel density opens correctly at another. Pages are honoured as
 * stored, then grown to `pages` if the palette needs more room. Corrupt or truncated data yields an
 * unpainted site rather than an exception — a broken splat should cost the paint, not the project.
 */
export function deserializeSurfaceMap(
  state: TerrainSurfaceState | undefined,
  widthMeters: number,
  lengthMeters: number,
  texelsPerMeter?: number,
  pages = 1,
): SurfaceMap {
  const storedPages = Math.min(
    MAX_SPLAT_PAGES,
    Math.max(1, Math.trunc(Number(state?.pages ?? 1)) || 1),
  );
  const map = SurfaceMap.forSite(
    widthMeters,
    lengthMeters,
    texelsPerMeter,
    Math.max(storedPages, pages),
  );
  if (!state || state.encoding !== 'rle-base64') return map;

  const storedWidth = Math.max(1, Math.trunc(state.width));
  const storedHeight = Math.max(1, Math.trunc(state.height));
  const stride = storedPages * 4;
  const stored = decodeRunLength(state.data, storedWidth * storedHeight, stride);
  if (!stored) return map;

  const copyTexel = (src: number, dst: number): void => {
    for (let page = 0; page < storedPages; page += 1) {
      const from = src * stride + page * 4;
      const to = map.offset(dst, page);
      for (let c = 0; c < 4; c += 1) map.data[to + c] = stored[from + c] ?? 0;
    }
  };

  if (storedWidth === map.width && storedHeight === map.height) {
    for (let i = 0; i < map.texelCount; i += 1) copyTexel(i, i);
    map.touch();
    return map;
  }

  for (let row = 0; row < map.height; row += 1) {
    const sr = Math.min(storedHeight - 1, Math.floor(((row + 0.5) / map.height) * storedHeight));
    for (let col = 0; col < map.width; col += 1) {
      const sc = Math.min(storedWidth - 1, Math.floor(((col + 0.5) / map.width) * storedWidth));
      copyTexel(sr * storedWidth + sc, row * map.width + col);
    }
  }
  map.touch();
  return map;
}

/** Decode the run stream back to `stride`-byte texels, or null when it does not describe them all. */
function decodeRunLength(base64: string, texelCount: number, stride: number): Uint8Array | null {
  const runs = base64ToBytes(base64);
  const record = stride + 2;
  if (!runs || runs.length % record !== 0) return null;
  const out = new Uint8Array(texelCount * stride);
  let written = 0;
  for (let i = 0; i < runs.length; i += record) {
    const count = (runs[i] ?? 0) | ((runs[i + 1] ?? 0) << 8);
    for (let n = 0; n < count; n += 1) {
      if (written >= texelCount) return null;
      out.set(runs.subarray(i + 2, i + record), written * stride);
      written += 1;
    }
  }
  return written === texelCount ? out : null;
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  // Chunked so a large map cannot blow the argument limit of String.fromCharCode.
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array | null {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

/* ----------------------------------- Helpers ----------------------------------- */

function cloneLayer(layer: SurfaceLayer): SurfaceLayer {
  return { ...layer, ...(layer.swatch ? { swatch: { ...layer.swatch } } : {}) };
}

function clamp(value: number, min: number, max: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
}

/** `#abc` and `#aabbcc` in, `#aabbcc` out. Anything else is not a colour. */
export function normalizeHex(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-f]{3}$/.test(trimmed)) {
    const [r, g, b] = [trimmed[1], trimmed[2], trimmed[3]];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return undefined;
}
